import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import toast from 'react-hot-toast';
import { StarIcon, CheckBadgeIcon, PlayIcon, ArrowLeftIcon, ClockIcon } from '@heroicons/react/24/solid';
import { StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline';

const OCCASION_CONFIG: Record<string, { label: string; emoji: string; title: string }> = {
  birthday: { label: 'Happy Birthday', emoji: '🎂', title: 'Send a Birthday ShoutOut' },
  roast: { label: 'Friendly Roast', emoji: '🔥', title: 'Send a Roast ShoutOut' },
  encouragement: { label: 'Advice or Encouragement', emoji: '💪', title: 'Get Advice or Encouragement with a ShoutOut' },
  announcement: { label: 'Make an Announcement', emoji: '📣', title: 'Make an Announcement with a ShoutOut' },
  other: { label: 'Other', emoji: '✨', title: 'Send a ShoutOut' },
  advice: { label: 'Get Advice', emoji: '💡', title: 'Get Advice with a ShoutOut' },
  anniversary: { label: 'Anniversary', emoji: '💍', title: 'Send an Anniversary ShoutOut' },
  corporate: { label: 'Corporate Event', emoji: '🏢', title: 'Book a Corporate ShoutOut' },
};

const HIDDEN_FROM_PROFILE = new Set(['express', 'gift', 'debate']);

const CYCLING_WORDS = ['Birthday', 'Roast', 'Anniversary', 'Encouragement', 'Announcement'];

interface TalentProfile {
  id: string;
  user_id: string;
  username?: string;
  full_name?: string;
  temp_full_name?: string;
  temp_avatar_url?: string;
  bio?: string;
  promo_video_url?: string;
  is_verified?: boolean;
  selected_shoutout_types?: string[];
  express_delivery_enabled?: boolean;
  express_delivery_price?: number;
  category?: string;
  fortis_vendor_id?: string;
  admin_fee_percentage?: number;
  fulfillment_time_hours?: number;
  pricing_mode?: 'single' | 'tiered';
  audio_shoutout_enabled?: boolean;
  audio_shoutout_price?: number;
  base_pricing?: number;
  current_pricing?: number;
}

interface BioSettings {
  gradient_start?: string;
  gradient_end?: string;
  gradient_direction?: string;
  button_color?: string;
  button_style?: string;
  text_color?: string;
  display_name?: string;
  profile_image_url?: string;
}

interface Review {
  id: string;
  rating: number;
  comment: string;
  created_at: string;
  users?: { full_name?: string };
}

interface RecentShoutout {
  id: string;
  recipient_name: string;
  occasion: string;
  video_url: string;
  created_at: string;
}

interface PricingData {
  base_pricing: number;
  current_pricing: number;
}

type PageStep = 'profile' | 'order' | 'login' | 'details' | 'complete';

const ShoutOutProfilePage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();

  const [talent, setTalent] = useState<TalentProfile | null>(null);
  const [bioSettings, setBioSettings] = useState<BioSettings | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [recentShoutouts, setRecentShoutouts] = useState<RecentShoutout[]>([]);
  const [pricing, setPricing] = useState<PricingData | null>(null);
  const [loading, setLoading] = useState(true);

  const [cycleIndex, setCycleIndex] = useState(0);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // FOMO notification state
  const [fomoOrders, setFomoOrders] = useState<{ name: string; created_at: string }[]>([]);
  const [fomoIndex, setFomoIndex] = useState(-1);
  const [fomoVisible, setFomoVisible] = useState(false);

  // Order state
  const [step, setStep] = useState<PageStep>('profile');
  const [selectedOccasion, setSelectedOccasion] = useState<string | null>(null);
  const [isExpress, setIsExpress] = useState(false);
  const [isAudio, setIsAudio] = useState(false);
  const [selectedTier, setSelectedTier] = useState<'30s' | '60s' | '2min' | null>(null);

  // Scheduled / "future event" order state. When true, payment is deferred:
  // the customer's card is vaulted now and LunarPay charges it 10 days before
  // the chosen event date.
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledEventDate, setScheduledEventDate] = useState<string>(''); // YYYY-MM-DD
  const [guestEmail, setGuestEmail] = useState('');
  const [emailValid, setEmailValid] = useState(false);
  // Track whether the field has been "committed" (blurred or Continue-clicked)
  // so we don't show the "invalid" error while the user is still typing — the
  // mid-typing red text used to appear before they'd finished writing
  // ".com".
  const [emailTouched, setEmailTouched] = useState(false);

  // Payment state
  const iframeContainerRef = useRef<HTMLDivElement>(null);
  const [commerceInstance, setCommerceInstance] = useState<any>(null);
  const [isPaymentReady, setIsPaymentReady] = useState(false);
  const [isTransactionIntent, setIsTransactionIntent] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const successHandledRef = useRef(false);
  // Granular payment status + slow-network detection. The bioapp checkout
  // chains 6+ network calls after the actual card charge (notifications,
  // webhook, monthly-donation addon, credit deduction). On bad internet
  // those used to silently stack into a 20–60s opaque "Processing..." with
  // no way for the fan to know the order already succeeded. Status text +
  // a stuck banner give them confidence to keep waiting instead of hitting
  // refresh and double-paying.
  const [paymentStatus, setPaymentStatus] = useState<
    'idle' | 'charging' | 'saving' | 'finalizing'
  >('idle');
  const [showSlowBanner, setShowSlowBanner] = useState(false);
  useEffect(() => {
    if (!isProcessing) { setShowSlowBanner(false); return; }
    const t = setTimeout(() => setShowSlowBanner(true), 15000);
    return () => clearTimeout(t);
  }, [isProcessing]);
  const processingLabel =
    paymentStatus === 'charging' ? 'Charging your card…' :
    paymentStatus === 'saving'   ? 'Saving your order…' :
    paymentStatus === 'finalizing' ? 'Finalizing…' :
    'Processing…';

  // Saved card state (LunarPay)
  const [savedCards, setSavedCards] = useState<any[]>([]);
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
  const [lpCustomerId, setLpCustomerId] = useState<number | null>(null);
  const [useNewCard, setUseNewCard] = useState(false);
  const [checkingCards, setCheckingCards] = useState(false);

  // Credits state
  const [userCredits, setUserCredits] = useState(0);
  const [applyCredits, setApplyCredits] = useState(false);

  // OTP login modal (triggered when existing user email detected)
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpPhone, setOtpPhone] = useState('');
  const [otpPhoneHint, setOtpPhoneHint] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpNeedsPhone, setOtpNeedsPhone] = useState(false);

  // Monthly supporter discount
  const [isMonthlySupporter, setIsMonthlySupporter] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<{ id: string; code: string; discount: number; discountType: string; validUntil?: string | null } | null>(null);
  const [couponSecondsLeft, setCouponSecondsLeft] = useState<number | null>(null);

  // Login + details state
  const [orderId, setOrderId] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [mention1, setMention1] = useState('');
  const [mention2, setMention2] = useState('');
  const [playingVideoId, setPlayingVideoId] = useState<string | null>(null);

  // Monthly donation add-on
  const [addMonthlySupport, setAddMonthlySupport] = useState(false);
  const [monthlySupportAmount, setMonthlySupportAmount] = useState('');

  // Cycling header
  useEffect(() => {
    const timer = setInterval(() => {
      setCycleIndex(prev => (prev + 1) % CYCLING_WORDS.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  // Auto-scroll reviews every 3 seconds
  useEffect(() => {
    if (reviews.length <= 1) return;
    const timer = setInterval(() => {
      setReviewIndex(prev => (prev + 1) % reviews.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [reviews.length]);

  // FOMO notification cycling — match the cadence of the main site's FOMO component
  useEffect(() => {
    if (fomoOrders.length === 0) return;
    let usedIndices = new Set<number>();

    const getUnused = (): number => {
      const available = fomoOrders.map((_, i) => i).filter(i => !usedIndices.has(i));
      if (available.length === 0) { usedIndices = new Set(); return Math.floor(Math.random() * fomoOrders.length); }
      return available[Math.floor(Math.random() * available.length)];
    };

    const showNext = () => {
      const idx = getUnused();
      usedIndices.add(idx);
      setFomoIndex(idx);
      setFomoVisible(true);
      setTimeout(() => setFomoVisible(false), 5000);
    };

    const initialDelay = 3000 + Math.random() * 2000;
    const initialTimer = setTimeout(() => {
      showNext();
      recurringTimer = setInterval(showNext, 8000);
    }, initialDelay);
    let recurringTimer: ReturnType<typeof setInterval>;

    return () => { clearTimeout(initialTimer); clearInterval(recurringTimer); };
  }, [fomoOrders]);

  // Pre-fill email from bio page subscription data or session
  useEffect(() => {
    const prefillEmail = async () => {
      let prefilled = '';
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.email) {
        prefilled = session.user.email;
        setGuestEmail(prefilled);
        setEmailValid(true);
      } else {
        const savedEmail = localStorage.getItem('bio_subscriber_email');
        if (savedEmail) {
          prefilled = savedEmail;
          setGuestEmail(savedEmail);
          setEmailValid(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(savedEmail));
        }
      }
      const isVerified = localStorage.getItem('bio_account_verified') === 'true';
      if (prefilled && isVerified) checkSavedCards(prefilled);
    };
    prefillEmail();
  }, []);

  // Fetch all data
  useEffect(() => {
    const fetchData = async () => {
      if (!username) return;
      try {
        let { data: profile } = await supabase
          .from('talent_profiles')
          .select('*, users!talent_profiles_user_id_fkey(full_name, avatar_url)')
          .eq('username', username)
          .single();

        if (!profile && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(username)) {
          const { data: profileById } = await supabase
            .from('talent_profiles')
            .select('*, users!talent_profiles_user_id_fkey(full_name, avatar_url)')
            .eq('id', username)
            .single();
          profile = profileById;
        }

        if (!profile) {
          navigate(`/${username}`);
          return;
        }
        setTalent(profile);

        const { data: settings } = await supabase
          .from('bio_settings')
          .select('*')
          .eq('talent_id', profile.id)
          .single();
        if (settings) setBioSettings(settings);

        const { data: pricingData } = await supabase
          .from('talent_pricing_urgency')
          .select('base_pricing, current_pricing')
          .eq('user_id', profile.user_id)
          .single();
        if (pricingData) setPricing(pricingData);

        const { data: reviewsData } = await supabase
          .from('reviews')
          .select('id, rating, comment, created_at, users(full_name)')
          .eq('talent_id', profile.id)
          .order('created_at', { ascending: false })
          .limit(10);
        const textReviews = (reviewsData || []).filter((r: any) => r.comment && String(r.comment).trim().length > 0);
        setReviews(textReviews as any);

        const { data: shoutoutsData } = await supabase
          .from('orders')
          .select('id, recipient_name, occasion, video_url, created_at')
          .eq('talent_id', profile.id)
          .eq('status', 'completed')
          .not('video_url', 'is', null)
          .order('completed_at', { ascending: false })
          .limit(6);
        if (shoutoutsData) setRecentShoutouts(shoutoutsData);

        // Fetch recent orders for FOMO notification
        const { data: fomoData } = await supabase
          .from('orders')
          .select('id, created_at, recipient_name, users!orders_user_id_fkey(full_name)')
          .eq('talent_id', profile.id)
          .in('status', ['completed', 'in_progress'])
          .order('created_at', { ascending: false })
          .limit(20);
        if (fomoData) {
          const formatted = (fomoData as any[])
            .map(o => ({
              name: o.users?.full_name?.split(' ')[0] || o.recipient_name?.split(' ')[0] || 'Someone',
              created_at: o.created_at,
            }))
            .filter(o => o.name && o.name !== 'Someone');
          setFomoOrders(formatted);
        }
      } catch (error) {
        console.error('Error fetching ShoutOut profile:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [username, navigate]);

  // Check if user is a monthly supporter of this talent
  useEffect(() => {
    const checkSupporter = async () => {
      const savedEmail = localStorage.getItem('bio_subscriber_email');
      if (!savedEmail || !talent?.id) return;
      try {
        const { data } = await supabase.functions.invoke('lunarpay-api', {
          body: { action: 'get-orders', email: savedEmail },
        });
        const monthlyOrder = (data?.orders || []).find((o: any) =>
          o.talent_id === talent.id && o.service_type === 'donation' && o.occasion === 'monthly_support' && o.status === 'completed'
        );
        if (monthlyOrder) setIsMonthlySupporter(true);
      } catch {}
    };
    checkSupporter();
  }, [talent?.id]);

  // Auto-apply promo coupon from URL param. Accepts ?promo= (legacy) and
  // ?coupon= (used by shoutout.us and our marketing emails) so a fan landing
  // here from any link gets their discount auto-applied.
  // Also enforces valid_from / valid_until (per-fan 24h codes from the
  // fan-gift email expire 24h after issue).
  useEffect(() => {
    const applyPromo = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('coupon') || params.get('promo');
      if (!code) return;
      try {
        const { data: coupon } = await supabase
          .from('coupons')
          .select('id, code, discount_type, discount_value, max_uses, used_count, is_active, valid_from, valid_until, talent_id')
          .eq('code', code.toUpperCase())
          .eq('is_active', true)
          .single();
        if (!coupon) return;
        // If the coupon is scoped to a specific creator, only honour it on their page
        if (coupon.talent_id && coupon.talent_id !== talent?.id) return;
        const now = Date.now();
        const usesOk = coupon.max_uses === null || coupon.used_count < coupon.max_uses;
        const startedOk = !coupon.valid_from || new Date(coupon.valid_from).getTime() <= now;
        const notExpired = !coupon.valid_until || new Date(coupon.valid_until).getTime() >= now;
        if (usesOk && startedOk && notExpired) {
          setAppliedCoupon({
            id: coupon.id,
            code: coupon.code,
            discount: Number(coupon.discount_value),
            discountType: coupon.discount_type,
            validUntil: coupon.valid_until,
          });
          // Start viewer-specific 24hr countdown stored in localStorage
          const timerKey = `viewer_coupon_timer_${coupon.code}`;
          const existing = localStorage.getItem(timerKey);
          if (!existing) {
            localStorage.setItem(timerKey, JSON.stringify({ code: coupon.code, expiresAt: Date.now() + 24 * 60 * 60 * 1000 }));
          }
          const timerData = JSON.parse(localStorage.getItem(timerKey) || '{}');
          const secsLeft = Math.max(0, Math.ceil((timerData.expiresAt - Date.now()) / 1000));
          setCouponSecondsLeft(secsLeft > 0 ? secsLeft : null);
        }
      } catch {}
    };
    applyPromo();
  }, []);

  // Countdown ticker for applied coupon timer
  useEffect(() => {
    if (couponSecondsLeft === null || couponSecondsLeft <= 0) return;
    const interval = setInterval(() => {
      setCouponSecondsLeft(prev => {
        if (prev === null || prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [couponSecondsLeft]);

  const formatCouponTime = (secs: number) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  // Derived values
  const displayName = talent?.full_name || talent?.temp_full_name || bioSettings?.display_name || (talent as any)?.users?.full_name || 'Creator';
  const firstName = displayName.split(' ')[0] || 'Creator';
  const profileImage = bioSettings?.profile_image_url || talent?.temp_avatar_url || (talent as any)?.users?.avatar_url;
  const accentColor = bioSettings?.button_color || '#3b82f6';

  // Update page title + OG meta tags when talent loads so link previews show creator name
  useEffect(() => {
    if (!talent) return;
    const pageTitle = `${displayName} | ShoutOut Fans`;
    const description = `Get a personalized video ShoutOut from ${displayName}!`;
    const pageUrl = `https://shoutout.fans/${talent.username || talent.id}`;

    document.title = pageTitle;

    const setMeta = (property: string, content: string, isName = false) => {
      const attr = isName ? 'name' : 'property';
      let el = document.querySelector(`meta[${attr}="${property}"]`) as HTMLMetaElement;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, property);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    setMeta('og:title', pageTitle);
    setMeta('og:description', description);
    setMeta('og:site_name', 'ShoutOut Fans');
    setMeta('og:type', 'profile');
    setMeta('og:url', pageUrl);
    if (profileImage) setMeta('og:image', profileImage);
    setMeta('twitter:card', 'summary', true);
    setMeta('twitter:title', pageTitle, true);
    setMeta('twitter:description', description, true);
    if (profileImage) setMeta('twitter:image', profileImage, true);
    setMeta('description', description, true);

    return () => { document.title = 'ShoutOut Fans'; };
  }, [displayName, profileImage, talent?.username, talent?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const gradStart = bioSettings?.gradient_start || '#0a0a0a';
  const gradEnd = bioSettings?.gradient_end || '#1a1a2e';
  const basePrice = pricing?.current_pricing || pricing?.base_pricing || 0;
  const avgRating = reviews.length > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length : 0;

  const occasions = (() => {
    let types = (talent?.selected_shoutout_types && talent.selected_shoutout_types.length > 0)
      ? talent.selected_shoutout_types.filter(o => !HIDDEN_FROM_PROFILE.has(o) && o !== 'corporate')
      : ['birthday', 'roast', 'encouragement', 'announcement', 'anniversary'];
    // Merge advice into encouragement
    if (types.includes('advice') && !types.includes('encouragement')) {
      types = types.map(t => t === 'advice' ? 'encouragement' : t);
    } else {
      types = types.filter(t => t !== 'advice');
    }
    // Swap "Celebrate a Win" → "Other" (preserves backwards compatibility for any
    // talent profiles still configured with celebrate in selected_shoutout_types)
    types = types.map(t => t === 'celebrate' ? 'other' : t);
    // Always include "Other" and force it to be the last item (bottom-right of grid)
    types = types.filter(t => t !== 'other');
    types.push('other');
    return types;
  })();

  const expressPrice = talent?.express_delivery_price || 0;
  const tierMultipliers: Record<string, number> = { '30s': 0.31, '60s': 0.54, '2min': 1.0 };
  const activeTier = talent?.pricing_mode === 'tiered' ? (selectedTier || '30s') : null;
  const effectiveBasePrice = isAudio && talent?.audio_shoutout_price ? talent.audio_shoutout_price :
    (activeTier ? Math.round(basePrice * (tierMultipliers[activeTier] || 1)) : basePrice);
  const rawOrderPrice = isExpress && expressPrice > 0 ? effectiveBasePrice + (expressPrice - basePrice) : effectiveBasePrice;
  const couponDiscount = appliedCoupon
    ? (appliedCoupon.discountType === 'percentage'
      ? Math.round(rawOrderPrice * (appliedCoupon.discount / 100) * 100) / 100
      : Math.min(appliedCoupon.discount, rawOrderPrice))
    : 0;
  const supporterDiscount = (isMonthlySupporter && !appliedCoupon) ? Math.round(rawOrderPrice * 0.10 * 100) / 100 : 0;
  const orderPrice = rawOrderPrice - couponDiscount - supporterDiscount;
  const adminFeePercent = 25;
  const adminFee = Math.round(rawOrderPrice * (adminFeePercent / 100) * 100) / 100;
  const processingFee = Math.round((orderPrice * 0.029 + 0.30) * 100) / 100;
  const totalPrice = Math.round((orderPrice + processingFee) * 100) / 100;
  const creditsToApply = applyCredits ? Math.min(userCredits, totalPrice) : 0;
  const amountAfterCredits = Math.max(0, totalPrice - creditsToApply);

  const fulfillmentDays = talent?.fulfillment_time_hours
    ? Math.round(talent.fulfillment_time_hours / 24)
    : 7;

  const getButtonRadius = () => {
    switch (bioSettings?.button_style) {
      case 'pill': return '9999px';
      case 'square': return '0.5rem';
      default: return '0.75rem';
    }
  };

  const getContrastText = (hex: string): string => {
    const c = (hex || '#3b82f6').replace('#', '');
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? '#000000' : '#ffffff';
  };

  // Select occasion -> go to order step
  const handleOccasionSelect = (occasion: string, scheduled = false) => {
    setSelectedOccasion(occasion);
    setStep('order');
    setIsExpress(false);
    setIsScheduled(scheduled);
    if (!scheduled) setScheduledEventDate('');
    if (talent?.pricing_mode === 'tiered' && !selectedTier) setSelectedTier('30s');
    if (!guestEmail) {
      setGuestEmail('');
      setEmailValid(false);
    }
    setIsPaymentReady(false);
    setIsTransactionIntent(false);
    setCommerceInstance(null);
    setIsProcessing(false);
    successHandledRef.current = false;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Schedule for later: reuse the regular order flow but with isScheduled = true.
  // We default the occasion to "other" so the talent has context but the
  // customer can update it on the form.
  const handleScheduleClick = () => {
    handleOccasionSelect('other', true);
  };

  const handleBackToProfile = () => {
    setStep('profile');
    setSelectedOccasion(null);
    setIsPaymentReady(false);
    setIsTransactionIntent(false);
    setCommerceInstance(null);
    successHandledRef.current = false;
  };

  // Email change -> just update text. We deliberately do NOT validate on
  // keystroke (avoids the "looks valid at .co" mid-typing race that kicks
  // off OTP/Fortis init before the user has finished writing ".com").
  // Validation happens on blur, on Enter, or when the explicit Continue
  // button is tapped (mobile keyboards' "Done" key doesn't always blur).
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGuestEmail(e.target.value);
    // While the user is editing again, suppress the error text and clear
    // any prior "valid" state so the form doesn't sit half-progressed on a
    // partially-edited address.
    setEmailTouched(false);
    if (emailValid) {
      setEmailValid(false);
      // Destroy the current Fortis Elements instance so a fresh one (with a
      // new intention tied to the new email) is created when validation
      // passes again. Without this the old iframe stays mounted and
      // silently submits under the original email.
      setCommerceInstance(null);
      setIsPaymentReady(false);
      setIsTransactionIntent(false);
      successHandledRef.current = false;
    }
  };

  const validateAndCommitEmail = async () => {
    setEmailTouched(true);
    const trimmed = guestEmail.trim();
    if (trimmed !== guestEmail) setGuestEmail(trimmed);
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmed);
    setEmailValid(valid);
    if (!valid) return;

    // Bio-page ShoutOut purchases use a transaction-type Fortis intention
    // (Fortis charges the card directly inside Elements), so we don't vault
    // the card or pre-create a LunarPay customer here. That work belongs to
    // the wallet-reload flow on adscenter, not to one-shot fan purchases.

    // Check if this email belongs to an existing user with a phone
    try {
      const { data } = await supabase.functions.invoke('send-registration-otp', {
        body: { email: guestEmail.toLowerCase().trim(), checkEmailOnly: true },
      });
      if (data?.sentToExistingPhone) {
        setOtpPhoneHint(data.phoneHint || '');
        setOtpPhone(data.phone || '');
        setOtpSent(true);
        setOtpNeedsPhone(false);
        setShowOtpModal(true);
        return;
      }
    } catch {}
    // New user — no saved cards to load, Fortis will init automatically
  };

  const checkSavedCards = async (email: string) => {
    setCheckingCards(true);
    try {
      const { data, error } = await supabase.functions.invoke('lunarpay-api', {
        body: { action: 'get-payment-methods', email: email.toLowerCase().trim() },
      });
      if (!error && data?.paymentMethods?.length > 0) {
        setSavedCards(data.paymentMethods);
        setSelectedCardId(data.paymentMethods.find((c: any) => c.isDefault)?.id || data.paymentMethods[0].id);
        // Also look up their LP customer ID
        const { data: custData } = await supabase.functions.invoke('lunarpay-api', {
          body: { action: 'find-or-create-customer', email: email.toLowerCase().trim() },
        });
        if (custData?.customerId) setLpCustomerId(custData.customerId);
      } else {
        setSavedCards([]);
        setSelectedCardId(null);
      }
      // Also fetch credits
      const { data: acctData } = await supabase.functions.invoke('lunarpay-api', {
        body: { action: 'get-account', email: email.toLowerCase().trim() },
      });
      if (acctData?.user?.credits) setUserCredits(parseFloat(acctData.user.credits) || 0);
    } catch (e) {
      console.log('Saved card check failed:', e);
      setSavedCards([]);
    } finally {
      setCheckingCards(false);
    }
  };

  const handleOtpSend = async () => {
    if (!otpPhone.trim()) { toast.error('Enter your phone number'); return; }
    setOtpLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-registration-otp', {
        body: { email: guestEmail.toLowerCase().trim(), phone: otpPhone },
      });
      if (error) throw error;
      setOtpPhoneHint(data?.phoneHint || otpPhone.slice(-4));
      setOtpSent(true);
      toast.success('Code sent!');
    } catch (err: any) { toast.error(err?.message || 'Failed to send code'); }
    finally { setOtpLoading(false); }
  };

  const handleOtpVerify = async () => {
    if (otpCode.length < 4) { toast.error('Enter the code'); return; }
    setOtpLoading(true);
    try {
      const { error } = await supabase.functions.invoke('verify-registration-otp', {
        body: { email: guestEmail.toLowerCase().trim(), phone: otpPhone, code: otpCode },
      });
      if (error) throw error;
      toast.success('Verified!');
      localStorage.setItem('bio_subscriber_email', guestEmail.toLowerCase().trim());
      localStorage.setItem('bio_account_verified', 'true');
      await checkSavedCards(guestEmail);
      setShowOtpModal(false);
    } catch (err: any) { toast.error(err?.message || 'Invalid code'); }
    finally { setOtpLoading(false); }
  };

  const processMonthlyDonationAddon = async () => {
    if (!addMonthlySupport || !monthlySupportAmount || parseFloat(monthlySupportAmount) < 1 || !talent) return;
    const donAmt = parseFloat(monthlySupportAmount);
    const donFeeRate = 0.029;
    const donFeeFlat = 0.30;
    const donFee = Math.round((donAmt * donFeeRate + donFeeFlat) * 100) / 100;
    const donTotal = Math.round((donAmt + donFee) * 100);
    try {
      // Charge the saved card now (first month) and start the recurring
      // subscription. Capture the LunarPay charge id so the donation row
      // gets a unique payment_transaction_id — the previous code wrote
      // the literal string 'SHOUTOUT_ADDON' which collided as soon as a
      // second supporter signed up.
      let addonTxnId: string | null = null;
      if (selectedCardId && lpCustomerId) {
        const { data: chargeData } = await supabase.functions.invoke('lunarpay-api', {
          body: { action: 'charge', customerId: lpCustomerId, paymentMethodId: selectedCardId, amount: donTotal, description: `Monthly Support ${displayName}` },
        });
        const ch = chargeData?.charge;
        addonTxnId =
          ch?.fortisTransactionId ||
          (ch?.id != null ? `LP_${ch.id}` : null);
        await supabase.functions.invoke('lunarpay-api', {
          body: { action: 'create-subscription', customerId: lpCustomerId, paymentMethodId: selectedCardId, amount: donTotal, frequency: 'monthly' },
        });
      }
      const { data: donOrderId } = await supabase.rpc('create_guest_order', {
        p_guest_email: guestEmail.toLowerCase().trim(),
        p_talent_id: talent.id,
        p_occasion: 'monthly_support',
        p_amount: donTotal,
        p_admin_fee: 0,
        p_fulfillment_deadline: new Date(2099, 0, 1).toISOString(),
        p_payment_transaction_id: addonTxnId,
        p_payment_transaction_payload: { type: 'donation', monthly: true, amount: donAmt },
        p_promo_source: 'fans',
        p_is_express_delivery: false,
      });
      if (donOrderId) {
        await supabase.functions.invoke('lunarpay-api', {
          body: { action: 'update-order', orderId: donOrderId, updates: { service_type: 'donation', status: 'completed' } },
        }).catch(() => {});
      }
      toast.success(`Monthly support of $${donAmt.toFixed(2)} started!`);
    } catch (e: any) {
      console.error('Monthly donation addon error:', e);
      toast.error('ShoutOut order succeeded, but monthly support setup failed. You can set it up from the support page.');
    }
  };

  const handlePayWithSavedCard = async () => {
    if (!selectedCardId || !lpCustomerId || !talent || !selectedOccasion) return;

    // ---- Scheduled order with saved card ----
    // We don't charge the card now — instead we schedule a future charge via
    // LunarPay payment-schedules. Same vault, no immediate transaction.
    if (isScheduled) {
      if (!scheduledEventDate) { toast.error('Please select an event date'); return; }
      const eventDate = new Date(scheduledEventDate + 'T00:00:00');
      const minDate = new Date();
      minDate.setHours(0, 0, 0, 0);
      minDate.setDate(minDate.getDate() + 11);
      const maxDate = new Date();
      maxDate.setMonth(maxDate.getMonth() + 6);
      if (eventDate < minDate) { toast.error('Event date must be at least 11 days away'); return; }
      if (eventDate > maxDate) { toast.error('Event date must be within the next 6 months'); return; }

      const chargeDate = new Date(eventDate);
      chargeDate.setDate(chargeDate.getDate() - 10);
      const chargeDateISO = chargeDate.toISOString().slice(0, 10);

      setIsProcessing(true);
      setPaymentStatus('saving');
      try {
        const totalCents = Math.round(totalPrice * 100);
        const { data: schedData, error: schedErr } = await supabase.functions.invoke('lunarpay-api', {
          body: {
            action: 'create-payment-schedule',
            customerId: lpCustomerId,
            paymentMethodId: selectedCardId,
            description: `ShoutOut: ${selectedOccasion} from ${displayName} (event ${scheduledEventDate})`,
            payments: [{ amount: totalCents, date: chargeDateISO }],
          },
        });
        if (schedErr || !schedData?.schedule?.id) {
          throw new Error(schedData?.error || schedErr?.message || 'Could not create payment schedule');
        }
        const lpScheduleId = schedData.schedule.id;

        const fulfillmentDeadline = new Date(eventDate);
        fulfillmentDeadline.setDate(fulfillmentDeadline.getDate() - 1);
        const { data: newOrderId, error: orderError } = await supabase.rpc('create_guest_order', {
          p_guest_email: guestEmail.toLowerCase().trim(),
          p_talent_id: talent.id,
          p_occasion: selectedOccasion,
          p_amount: totalCents,
          p_original_amount: appliedCoupon ? Math.round(rawOrderPrice * 100) : null,
          p_discount_amount: appliedCoupon ? Math.round(couponDiscount * 100) : null,
          p_coupon_id: appliedCoupon?.id || null,
          p_coupon_code: appliedCoupon?.code || null,
          p_admin_fee: Math.round(adminFee * 100),
          p_fulfillment_deadline: fulfillmentDeadline.toISOString(),
          p_payment_transaction_id: `SCHEDULED:${lpScheduleId}`,
          p_payment_transaction_payload: { lunarpay_schedule_id: lpScheduleId },
          p_promo_source: 'fans',
          p_is_express_delivery: false,
          p_is_scheduled: true,
          p_scheduled_event_date: scheduledEventDate,
          p_scheduled_charge_date: chargeDateISO,
          p_lunarpay_customer_id: String(lpCustomerId),
          p_lunarpay_payment_method_id: String(selectedCardId),
          p_lunarpay_schedule_id: String(lpScheduleId),
        });
        if (orderError) throw orderError;

        setOrderId(newOrderId as string);
        setStep('login');
        setIsProcessing(false);
        setPaymentStatus('idle');
        toast.success('Scheduled! Your card will be charged 10 days before the event.');
      } catch (e: any) {
        console.error('Scheduled saved-card error:', e);
        toast.error(e.message || 'Could not schedule order');
        setIsProcessing(false);
        setPaymentStatus('idle');
      }
      return;
    }
    // ---- end scheduled branch ----

    setIsProcessing(true);
    setPaymentStatus('charging');
    try {
      const amtCents = Math.round(totalPrice * 100);
      const { data, error } = await supabase.functions.invoke('lunarpay-api', {
        body: {
          action: 'charge',
          customerId: lpCustomerId,
          paymentMethodId: selectedCardId,
          amount: amtCents,
          description: `ShoutOut: ${selectedOccasion} from ${displayName}`,
        },
      });
      if (error || !data?.charge) throw new Error(data?.error || error?.message || 'Charge failed');

      // LunarPay always assigns a numeric `id` to a successful charge, and
      // Fortis returns a `fortisTransactionId` for the underlying card
      // settlement. Prefer the Fortis id when present; either one is
      // globally unique. If somehow neither is present, treat it as a
      // failed charge rather than writing a sentinel that would collide
      // with the next saved-card customer.
      const savedCardTxnId =
        data.charge.fortisTransactionId ||
        (data.charge.id != null ? `LP_${data.charge.id}` : null);
      if (!savedCardTxnId) throw new Error('Charge succeeded but no transaction id returned');

      setPaymentStatus('saving');

      const feeCents = Math.round(adminFee * 100);
      const fulfillmentDeadline = new Date();
      fulfillmentDeadline.setHours(fulfillmentDeadline.getHours() + (talent.fulfillment_time_hours || 168));

      const { data: ordId, error: orderError } = await supabase.rpc('create_guest_order', {
        p_guest_email: guestEmail.toLowerCase().trim(),
        p_talent_id: talent.id,
        p_occasion: selectedOccasion,
        p_amount: amtCents,
        p_original_amount: appliedCoupon ? Math.round(rawOrderPrice * 100) : null,
        p_discount_amount: appliedCoupon ? Math.round(couponDiscount * 100) : null,
        p_coupon_id: appliedCoupon?.id || null,
        p_coupon_code: appliedCoupon?.code || null,
        p_admin_fee: feeCents,
        p_fulfillment_deadline: fulfillmentDeadline.toISOString(),
        p_payment_transaction_id: savedCardTxnId,
        p_payment_transaction_payload: data.charge,
        p_promo_source: 'fans',
        p_is_express_delivery: isExpress,
      });
      if (orderError) throw orderError;

      // Advance UI immediately; everything below is best-effort cleanup.
      setOrderId(ordId as string);
      setStep('login');
      setIsProcessing(false);
      setPaymentStatus('idle');
      toast.success('Payment successful!');

      void (async () => {
        try {
          if (isAudio || selectedTier) {
            await supabase.from('orders').update({ is_audio_shoutout: isAudio, shoutout_tier: selectedTier }).eq('id', ordId);
          }
          if (creditsToApply > 0) {
            const { data: acctData } = await supabase.functions.invoke('lunarpay-api', { body: { action: 'get-account', email: guestEmail.toLowerCase().trim() } });
            if (acctData?.user?.id) await supabase.rpc('use_credits_for_order', { p_user_id: acctData.user.id, p_order_id: ordId, p_order_amount_cents: Math.round(totalPrice * 100) });
          }
          await sendOrderNotifications(ordId as string);
          await processMonthlyDonationAddon();
        } catch (bgErr) {
          console.error('Post-success background tasks failed (order is safe):', bgErr);
        }
      })();
    } catch (e: any) {
      console.error('Saved card payment error:', e);
      toast.error(e.message || 'Payment failed');
      setIsProcessing(false);
      setPaymentStatus('idle');
    }
  };

  const sendOrderNotifications = async (oid: string) => {
    try {
      if (!talent || !selectedOccasion) return;
      const amtCents = Math.round(totalPrice * 100);

      if (appliedCoupon?.id) {
        try { await supabase.rpc('increment_coupon_usage', { p_coupon_id: appliedCoupon.id }); } catch {}
      }

      supabase.functions.invoke('lunarpay-api', {
        body: { action: 'ensure-auth-user', email: guestEmail.toLowerCase().trim() },
      }).catch(() => {});

      await supabase.rpc('notify_new_order', {
        p_order_id: oid,
        p_talent_id: talent.id,
        p_customer_email: guestEmail.toLowerCase().trim(),
        p_customer_name: guestEmail.split('@')[0],
        p_amount: amtCents,
        p_occasion: selectedOccasion,
        p_is_express: isExpress,
      });

      const { data: tpData } = await supabase.from('talent_profiles').select('user_id').eq('id', talent.id).single();
      let talentEmail = '';
      if (tpData?.user_id) {
        const { data: tUser } = await supabase.from('users').select('email').eq('id', tpData.user_id).single();
        talentEmail = tUser?.email || '';
      }

      if (talentEmail) {
        // Generate a one-click sign-in link for creator.shoutout.us
        let dashboardLink = 'https://creator.shoutout.us';
        try {
          const { data: sessionData } = await supabase.functions.invoke('creator-session', {
            body: { talent_id: talent.id, email: talentEmail },
          });
          if (sessionData?.hashed_token) {
            dashboardLink = `https://creator.shoutout.us/dashboard?token=${sessionData.hashed_token}`;
          }
        } catch (_) { /* fall back to plain link */ }

        await supabase.functions.invoke('send-mailgun', {
          body: {
            to: talentEmail,
            subject: `New ShoutOut Order from ${guestEmail.split('@')[0]}!`,
            html: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:20px;"><h1 style="color:#111827;">New ShoutOut Order!</h1><p>Hi ${displayName},</p><p>You have a new ${selectedOccasion || 'ShoutOut'} order!</p><div style="background:#eff6ff;border-radius:10px;padding:20px;margin:20px 0;"><p><strong>From:</strong> ${guestEmail.split('@')[0]}</p><p><strong>Amount:</strong> $${totalPrice.toFixed(2)}</p><p><strong>Occasion:</strong> ${selectedOccasion || 'ShoutOut'}</p></div><p><a href="${dashboardLink}" style="background:#3b82f6;color:white;padding:12px 24px;border-radius:8px;text-decoration:none;display:inline-block;">View &amp; Fulfill Order →</a></p><p style="color:#6b7280;font-size:12px;margin-top:16px;">This link signs you in automatically — no password needed.</p></div>`,
          }
        });
      }

      await supabase.functions.invoke('send-mailgun', {
        body: {
          to: guestEmail.toLowerCase().trim(),
          subject: `Your ShoutOut from ${displayName} is confirmed!`,
          html: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:20px;"><h1 style="color:#111827;">Order Confirmed!</h1><p>Hi ${guestEmail.split('@')[0]},</p><p>Your personalized ${selectedOccasion || 'ShoutOut'} video from <strong>${displayName}</strong> is confirmed!</p><div style="background:#f0fdf4;border-radius:10px;padding:20px;margin:20px 0;"><p><strong>Amount:</strong> $${totalPrice.toFixed(2)}</p><p><strong>Occasion:</strong> ${selectedOccasion || 'ShoutOut'}</p><p><strong>Delivery:</strong> ${isExpress ? '24 hours' : `${Math.ceil((talent?.fulfillment_time_hours || 168) / 24)} days`}</p></div><p>We'll send you a form to add details for your video. Once ${displayName} records it, you'll receive it via email.</p><p style="color:#6b7280;font-size:12px;margin-top:20px;">100% money-back guarantee if not delivered on time.</p></div>`,
        }
      });

      await supabase.functions.invoke('send-order-webhook', {
        body: { order_id: oid, customer_name: guestEmail.split('@')[0], customer_email: guestEmail, talent_name: displayName, talent_id: talent.id, amount: totalPrice, occasion: selectedOccasion, source: 'shoutout.fans', promo_source: 'fans', is_audio: isAudio, shoutout_tier: selectedTier }
      });

      // NOTE: Talent SMS for new orders is handled server-side by the
      // notify-talent-new-order edge function (triggered via a Database Webhook
      // on orders INSERT). Do NOT add a client-side send-sms call here — it
      // would cause a duplicate text from a different number (659 vs 217).
    } catch (e) {
      console.log('Notification error (non-blocking):', e);
    }
  };

  const initializeFortis = useCallback(async () => {
    if (!talent || !selectedOccasion) return;

    try {
      // Bio-page ShoutOut is one-shot: pass the amount so LunarPay returns a
      // transaction-type intention. Fortis Elements will charge the card
      // directly when the customer submits — we skip the
      // /customers, /payment-methods, and /charges round-trips entirely
      // (those belong to the wallet-reload flow where the card is vaulted
      // for auto-refuel).
      //
      // For scheduled "future event" orders, we instead need a ticket-type
      // intention so we can vault the card and create a LunarPay payment
      // schedule. lunarpay-intention defaults to hasRecurring=true (ticket
      // mode) when no amount is provided.
      const intentionBody: Record<string, unknown> = isScheduled
        ? {}
        : { amount: Math.max(1, Math.round(totalPrice * 100)) };
      const { data: intentionData, error: intentionError } = await supabase.functions.invoke('lunarpay-intention', {
        body: intentionBody,
      });

      if (intentionError || !intentionData?.clientToken) {
        throw new Error(intentionError?.message || 'Failed to create payment intention');
      }

      const { clientToken, intentionType } = intentionData;
      const isTransactionIntention = intentionType === 'transaction';
      setIsTransactionIntent(isTransactionIntention);

      const waitForCommerce = () =>
        new Promise<void>((resolve, reject) => {
          if ((window as any).Commerce?.elements) { resolve(); return; }
          if (!document.querySelector('script[src*="commercejs"]')) {
            const script = document.createElement('script');
            script.src = 'https://js.fortis.tech/commercejs-v1.0.0.min.js';
            script.async = true;
            document.body.appendChild(script);
          }
          let attempts = 0;
          const interval = setInterval(() => {
            attempts++;
            if ((window as any).Commerce?.elements) { clearInterval(interval); resolve(); }
            if (attempts > 50) { clearInterval(interval); reject(new Error('Commerce JS failed to load')); }
          }, 100);
        });

      await waitForCommerce();

      const ElementsCtor = (window as any).Commerce?.elements;
      if (!ElementsCtor) throw new Error('Commerce elements not available');

      const elements = new ElementsCtor(clientToken);

      const handlePaymentSuccess = async (payload: any) => {
        if (successHandledRef.current) return;
        successHandledRef.current = true;
        setIsProcessing(true);
        setPaymentStatus('charging');

        try {
          if (!talent || !selectedOccasion) throw new Error('Missing data');

          // ---- Scheduled "future event" order branch ----
          // The customer hasn't been charged yet. Fortis Elements has captured
          // a ticket_id; we vault that card via lunarpay-api save-card, create
          // a LunarPay payment-schedule for 10 days before the event date, and
          // insert the order with status='scheduled'. The hourly
          // sync-scheduled-orders cron will flip it to 'pending' once the
          // scheduled charge is collected.
          if (isScheduled) {
            if (!scheduledEventDate) throw new Error('Please select an event date');
            const eventDate = new Date(scheduledEventDate + 'T00:00:00');
            const minDate = new Date();
            minDate.setHours(0, 0, 0, 0);
            minDate.setDate(minDate.getDate() + 11);
            const maxDate = new Date();
            maxDate.setMonth(maxDate.getMonth() + 6);
            if (eventDate < minDate) throw new Error('Event date must be at least 11 days away');
            if (eventDate > maxDate) throw new Error('Event date must be within the next 6 months');

            const chargeDate = new Date(eventDate);
            chargeDate.setDate(chargeDate.getDate() - 10);
            const chargeDateISO = chargeDate.toISOString().slice(0, 10); // YYYY-MM-DD

            const ticketId = payload?.ticket?.id || payload?.data?.id || payload?.id;
            if (!ticketId) throw new Error('Card capture failed — no ticket id');

            setPaymentStatus('saving');

            // 1) find or create LunarPay customer
            const { data: customerData, error: custErr } = await supabase.functions.invoke('lunarpay-api', {
              body: {
                action: 'find-or-create-customer',
                email: guestEmail.toLowerCase().trim(),
                firstName: guestEmail.split('@')[0],
              },
            });
            if (custErr || !customerData?.customerId) {
              throw new Error(customerData?.error || custErr?.message || 'Could not create LunarPay customer');
            }
            const lpCustomerId = customerData.customerId;

            // 2) save the card
            const { data: cardData, error: cardErr } = await supabase.functions.invoke('lunarpay-api', {
              body: { action: 'save-card', customerId: lpCustomerId, ticketId, setDefault: true },
            });
            if (cardErr || !cardData?.paymentMethod?.id) {
              throw new Error(cardData?.error || cardErr?.message || 'Could not save card');
            }
            const lpPaymentMethodId = cardData.paymentMethod.id;

            // 3) create the payment schedule (single dated payment)
            const totalCents = Math.round(totalPrice * 100);
            const { data: schedData, error: schedErr } = await supabase.functions.invoke('lunarpay-api', {
              body: {
                action: 'create-payment-schedule',
                customerId: lpCustomerId,
                paymentMethodId: lpPaymentMethodId,
                description: `ShoutOut: ${selectedOccasion} from ${displayName} (event ${scheduledEventDate})`,
                payments: [{ amount: totalCents, date: chargeDateISO }],
              },
            });
            if (schedErr || !schedData?.schedule?.id) {
              throw new Error(schedData?.error || schedErr?.message || 'Could not create payment schedule');
            }
            const lpScheduleId = schedData.schedule.id;

            // 4) insert the order via the regular guest-order RPC —
            //    scheduled params are passed directly so the RPC sets
            //    status='scheduled' atomically inside the security definer
            //    block (no separate UPDATE needed).
            const fulfillmentDeadline = new Date(eventDate);
            fulfillmentDeadline.setDate(fulfillmentDeadline.getDate() - 1); // due day before event
            const { data: newOrderId, error: orderError } = await supabase.rpc('create_guest_order', {
              p_guest_email: guestEmail.toLowerCase().trim(),
              p_talent_id: talent.id,
              p_occasion: selectedOccasion,
              p_amount: totalCents,
              p_original_amount: appliedCoupon ? Math.round(rawOrderPrice * 100) : null,
              p_discount_amount: appliedCoupon ? Math.round(couponDiscount * 100) : null,
              p_coupon_id: appliedCoupon?.id || null,
              p_coupon_code: appliedCoupon?.code || null,
              p_admin_fee: Math.round(adminFee * 100),
              p_fulfillment_deadline: fulfillmentDeadline.toISOString(),
              p_payment_transaction_id: `SCHEDULED:${lpScheduleId}`,
              p_payment_transaction_payload: { lunarpay_schedule_id: lpScheduleId, ticket: payload },
              p_promo_source: 'fans',
              p_is_express_delivery: false,
              p_is_scheduled: true,
              p_scheduled_event_date: scheduledEventDate,
              p_scheduled_charge_date: chargeDateISO,
              p_lunarpay_customer_id: String(lpCustomerId),
              p_lunarpay_payment_method_id: String(lpPaymentMethodId),
              p_lunarpay_schedule_id: String(lpScheduleId),
            });
            if (orderError) throw orderError;

            setOrderId(newOrderId as string);
            setStep('login');
            setIsProcessing(false);
            setPaymentStatus('idle');
            toast.success('Scheduled! Your card will be charged 10 days before the event.');
            return;
          }
          // ---- end scheduled branch ----

          const amtCents = Math.round(totalPrice * 100);
          const feeCents = Math.round(adminFee * 100);

          // Transaction-type intention: Fortis Elements has already charged
          // the card by the time this fires. The payload carries the Fortis
          // transaction id — we just record the order and move on. No
          // backend round-trip to /customers, /payment-methods, or /charges.
          //
          // Fortis sale payload shape (payment_success / done):
          //   { '@type': 'done', data: { '@action': 'sale',
          //                              id: '<txn id>',
          //                              status_code: 101 (approved), ... } }
          // The txn id lives at payload.data.id when @action === 'sale'.
          // payload.data.id ALSO exists in ticket-type intentions, but
          // there it's a vault ticket id (not a charge), so we only trust
          // it when the action is an actual sale.
          let lpResult: any = null;
          const payloadAction = payload?.data?.['@action'] || payload?.['@action'];
          const isSaleAction = payloadAction === 'sale';
          const saleStatusCode = payload?.data?.status_code ?? payload?.status_code;
          const isSaleApproved =
            isSaleAction &&
            typeof saleStatusCode === 'number' &&
            saleStatusCode >= 100 &&
            saleStatusCode < 200;
          let fortisTransactionId: string | null =
            payload?.transaction?.id ||
            payload?.data?.transaction_id ||
            payload?.data?.transactionId ||
            payload?.transactionId ||
            payload?.transaction_id ||
            (isSaleApproved ? (payload?.data?.id ?? null) : null) ||
            null;

          if (isTransactionIntention) {
            // Hard-stop on a declined / errored sale so we never persist
            // an "order" that the customer wasn't actually charged for.
            if (isSaleAction && !isSaleApproved) {
              const reason =
                payload?.data?.response_message ||
                payload?.data?.verbiage ||
                `Payment declined (status ${saleStatusCode ?? 'unknown'})`;
              throw new Error(reason);
            }
            if (!fortisTransactionId) {
              // No id and no recognizable failure signal — refuse to write
              // a sentinel string. Better to surface "Payment failed" than
              // to silently strand the customer behind a duplicate-key
              // collision on the next order.
              throw new Error('Payment did not return a transaction id');
            }
          } else {
            // Legacy ticket-type fallback: vault + charge through lunarpay-api.
            // Should only hit if the intention call fell back to ticket mode.
            const ticketId = payload?.ticket?.id || payload?.data?.id || payload?.id;
            const { data: lpData, error: lpError } = await supabase.functions.invoke('lunarpay-api', {
              body: {
                action: 'process-payment',
                email: guestEmail.toLowerCase().trim(),
                ticketId,
                amount: amtCents,
                description: `ShoutOut: ${selectedOccasion} from ${displayName}`,
              },
            });
            if (lpError || !lpData?.chargeId) {
              throw new Error(lpData?.error || lpError?.message || 'Payment processing failed');
            }
            lpResult = lpData;
            fortisTransactionId = lpData.transactionId || lpData.chargeId?.toString() || fortisTransactionId;
            if (!fortisTransactionId) {
              throw new Error('Payment processed but no transaction id returned');
            }
          }

          setPaymentStatus('saving');

          const fulfillmentDeadline = new Date();
          fulfillmentDeadline.setHours(fulfillmentDeadline.getHours() + (talent.fulfillment_time_hours || 168));

          const { data: newOrderId, error: orderError } = await supabase.rpc('create_guest_order', {
            p_guest_email: guestEmail.toLowerCase().trim(),
            p_talent_id: talent.id,
            p_occasion: selectedOccasion,
            p_amount: amtCents,
            p_original_amount: appliedCoupon ? Math.round(rawOrderPrice * 100) : null,
            p_discount_amount: appliedCoupon ? Math.round(couponDiscount * 100) : null,
            p_coupon_id: appliedCoupon?.id || null,
            p_coupon_code: appliedCoupon?.code || null,
            p_admin_fee: feeCents,
            p_fulfillment_deadline: fulfillmentDeadline.toISOString(),
            p_payment_transaction_id: fortisTransactionId,
            p_payment_transaction_payload: lpResult ? { ...payload, lunarpay: lpResult } : payload,
            p_promo_source: 'fans',
            p_is_express_delivery: isExpress,
          });

          if (orderError) throw orderError;

          // Order is recorded — advance the fan IMMEDIATELY. Notifications,
          // credit deduction, and the monthly-donation upsell each round-trip
          // through 1-3 network calls; awaiting them here would tack 5–20s
          // of opaque "Processing..." onto the spinner on bad connections.
          // Run them in the background; they're best-effort and tolerate
          // the page navigating away (Supabase invoke returns immediately
          // once queued, edge functions complete server-side regardless).
          setOrderId(newOrderId as string);
          setStep('login');
          setIsProcessing(false);
          setPaymentStatus('idle');
          toast.success('Payment successful!');

          void (async () => {
            try {
              if (creditsToApply > 0) {
                const { data: acctData } = await supabase.functions.invoke('lunarpay-api', { body: { action: 'get-account', email: guestEmail.toLowerCase().trim() } });
                if (acctData?.user?.id) await supabase.rpc('use_credits_for_order', { p_user_id: acctData.user.id, p_order_id: newOrderId, p_order_amount_cents: Math.round(totalPrice * 100) });
              }
              await sendOrderNotifications(newOrderId as string);
              await processMonthlyDonationAddon();
            } catch (bgErr) {
              console.error('Post-success background tasks failed (order is safe):', bgErr);
            }
          })();
        } catch (error: any) {
          console.error('Error processing payment:', error);
          toast.error(error.message || 'Payment failed. Contact support.');
          setIsProcessing(false);
          setPaymentStatus('idle');
        }
      };

      elements.eventBus.on('ready', () => setIsPaymentReady(true));
      elements.eventBus.on('payment_success', handlePaymentSuccess);
      elements.eventBus.on('success', handlePaymentSuccess);
      elements.eventBus.on('done', handlePaymentSuccess);
      elements.eventBus.on('payment_error', (e: any) => {
        successHandledRef.current = false;
        setIsProcessing(false);
        toast.error(e?.message || 'Payment failed. Please try again.');
      });
      elements.eventBus.on('error', (e: any) => {
        successHandledRef.current = false;
        setIsProcessing(false);
        toast.error(e?.message || 'Payment error. Please try again.');
      });

      const themeBg = gradStart;
      const themeBtn = accentColor;

      elements.create({
        container: '#fortis-payment-container',
        theme: 'dark',
        environment: 'production',
        view: 'default',
        language: 'en-us',
        defaultCountry: 'US',
        floatingLabels: true,
        showReceipt: false,
        showSubmitButton: false,
        showValidationAnimation: true,
        hideAgreementCheckbox: !isTransactionIntention,
        hideTotal: true,
        appearance: {
          colorBackground: themeBg,
          colorButtonSelectedBackground: themeBtn,
          colorButtonSelectedText: '#ffffff',
          colorText: bioSettings?.text_color || '#ffffff',
          fontFamily: 'SourceSans',
          fontSize: '16px',
          borderRadius: '8px',
        },
      });

      setCommerceInstance(elements);
    } catch (error: any) {
      console.error('Error initializing payment:', error);
      toast.error('Failed to load payment form');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talent, selectedOccasion, totalPrice, adminFee, guestEmail, isExpress, gradStart, accentColor, isScheduled, scheduledEventDate]);

  // Initialize Fortis when email becomes valid and no saved cards (or user wants new card).
  // For scheduled orders we also wait until the customer has chosen an event date so the
  // payment form doesn't initialize before we have everything we need to vault + schedule.
  useEffect(() => {
    if (step !== 'order' || !emailValid || commerceInstance || checkingCards) return;
    if (savedCards.length > 0 && !useNewCard) return;
    if (isScheduled && !scheduledEventDate) return;
    initializeFortis();
  }, [step, emailValid, commerceInstance, initializeFortis, checkingCards, savedCards.length, useNewCard, isScheduled, scheduledEventDate]);

  // When express delivery is toggled on the order step, reset the Fortis instance so it
  // reinitializes with the updated total. Saved-card charges pass amount at charge time so
  // they don't need reinit — only the transaction-intent (new card) flow does.
  const prevIsExpressRef = useRef(isExpress);
  useEffect(() => {
    if (prevIsExpressRef.current === isExpress) return;
    prevIsExpressRef.current = isExpress;
    if (step !== 'order' || !emailValid) return;
    if (savedCards.length > 0 && !useNewCard) return; // saved card path: amount sent at charge time
    if (commerceInstance) {
      setCommerceInstance(null);
      setIsPaymentReady(false);
      successHandledRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpress]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2" style={{ borderColor: accentColor }} />
      </div>
    );
  }

  if (!talent) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <p className="text-gray-400">Profile not found</p>
      </div>
    );
  }

  const occasionConfig = selectedOccasion ? OCCASION_CONFIG[selectedOccasion] : null;

  return (
    <div
      className="min-h-screen"
      style={{ background: `linear-gradient(180deg, ${gradStart}, ${gradEnd})` }}
    >
      <div className="max-w-md mx-auto px-4 py-6">

        {/* ============== STEP 1: PROFILE ============== */}
        {step === 'profile' && (
          <>
            {/* Back to bio + ShoutOut logo */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => navigate(`/${username}`)}
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                <span className="text-sm">Back</span>
              </button>
              <img src="/shoutoutwhite.png" alt="ShoutOut" className="h-6 opacity-60" />
            </div>

            {/* Cycling Header */}
            <div className="text-center mb-6">
              <div className="h-7 overflow-hidden relative">
                {CYCLING_WORDS.map((word, i) => (
                  <div
                    key={word}
                    className="absolute inset-x-0 transition-all duration-500 ease-in-out"
                    style={{
                      transform: i === cycleIndex ? 'translateY(0)' : i === (cycleIndex - 1 + CYCLING_WORDS.length) % CYCLING_WORDS.length ? 'translateY(-100%)' : 'translateY(100%)',
                      opacity: i === cycleIndex ? 1 : 0,
                    }}
                  >
                    <p className="text-white text-lg font-bold">Get a Personalized <span style={{ background: 'linear-gradient(90deg, #60a5fa, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{word}</span> Video ShoutOut</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Profile Card — compact centered */}
            <div className="flex flex-col items-center mb-5">
              <div className="w-16 h-16 rounded-full overflow-hidden bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center mb-2 border-2 border-white/20 relative">
                {talent.promo_video_url && !videoPlaying ? (
                  <>
                    {profileImage && <img src={profileImage} alt={displayName} className="w-full h-full object-cover" />}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <button
                        onClick={() => { setVideoPlaying(true); setTimeout(() => videoRef.current?.play(), 100); }}
                        className="pointer-events-auto"
                      >
                        <PlayIcon className="h-5 w-5 text-white" />
                      </button>
                    </div>
                  </>
                ) : talent.promo_video_url && videoPlaying ? (
                  <video ref={videoRef} src={talent.promo_video_url} controls autoPlay playsInline className="w-full h-full object-cover" />
                ) : profileImage ? (
                  <img src={profileImage} alt={displayName} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white text-2xl font-bold">{displayName[0]}</span>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <h2 className="text-white font-bold text-base">{displayName}</h2>
                {talent.is_verified && <CheckBadgeIcon className="h-4 w-4 text-blue-400" />}
              </div>
              {reviews.length > 0 && (
                <div className="flex items-center gap-1 mt-1">
                  <StarIcon className="h-3.5 w-3.5 text-yellow-400" />
                  <span className="text-white text-xs font-medium">{avgRating.toFixed(1)}</span>
                  <span className="text-gray-500 text-xs">({reviews.length})</span>
                </div>
              )}
            </div>

            {/* Occasions Grid */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-semibold text-sm">Choose an occasion</h3>
                <span className="text-gray-400 text-[10px]">
                  {talent?.audio_shoutout_enabled ? 'Audio or Video' : 'Video ShoutOuts'}
                </span>
              </div>
              {appliedCoupon && (
                <div className="mb-3 p-3 rounded-xl border border-orange-500/30" style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(239,68,68,0.15))' }}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🔥</span>
                      <div>
                        <p className="text-white font-bold text-sm">{appliedCoupon.discountType === 'percentage' ? `${appliedCoupon.discount}% off` : `$${appliedCoupon.discount} off`} applied!</p>
                        <p className="text-orange-300 text-xs">Limited spots — discount applied automatically</p>
                      </div>
                    </div>
                    {couponSecondsLeft !== null && couponSecondsLeft > 0 && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <ClockIcon className="h-3.5 w-3.5 text-orange-400" />
                        <span className={`font-mono font-bold text-xs ${couponSecondsLeft < 600 ? 'text-red-400 animate-pulse' : couponSecondsLeft < 3600 ? 'text-amber-300' : 'text-orange-400'}`}>
                          {formatCouponTime(couponSecondsLeft)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                {occasions.map(occasion => {
                  const config = OCCASION_CONFIG[occasion] || { label: occasion, emoji: '🎬' };
                  return (
                    <button
                      key={occasion}
                      onClick={() => handleOccasionSelect(occasion)}
                      className="flex items-center gap-2.5 bg-white/5 border border-white/10 px-3 py-2.5 text-left hover:border-white/30 transition-all hover:scale-[1.02] active:scale-[0.98]"
                      style={{ borderRadius: getButtonRadius() }}
                    >
                      <span className="text-lg flex-shrink-0">{config.emoji}</span>
                      <span className="text-white font-medium text-sm">{config.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Schedule for future event — pay $0 today, card charged 10 days
                  before the event. Saved card → LunarPay payment-schedule. */}
              <button
                onClick={handleScheduleClick}
                className="mt-2 w-full flex items-center gap-3 px-3 py-3 text-left transition-all hover:scale-[1.01] active:scale-[0.99] border"
                style={{
                  borderRadius: getButtonRadius(),
                  background: `linear-gradient(135deg, ${accentColor}1f, ${accentColor}0a)`,
                  borderColor: `${accentColor}55`,
                }}
              >
                <span className="text-xl flex-shrink-0">📅</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-semibold text-sm">Need a video for a future event?</span>
                    <span
                      className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                      style={{ background: `${accentColor}40`, color: '#fff' }}
                    >$0 today</span>
                  </div>
                  <p className="text-gray-300 text-xs mt-0.5">Schedule a video for later. Card charged 10 days before.</p>
                </div>
              </button>
              {basePrice > 0 && (() => {
                const lowestTier = talent?.pricing_mode === 'tiered' ? Math.round(basePrice * 0.31) : basePrice;
                const audioP = talent?.audio_shoutout_enabled && talent?.audio_shoutout_price ? talent.audio_shoutout_price : Infinity;
                const hasOptions = talent?.pricing_mode === 'tiered' || (talent?.audio_shoutout_enabled && talent?.audio_shoutout_price);
                const lowest = Math.min(lowestTier, audioP === Infinity ? basePrice : audioP);
                const discountedLowest = appliedCoupon && appliedCoupon.discountType === 'percentage'
                  ? Math.round(lowest * (1 - appliedCoupon.discount / 100))
                  : appliedCoupon ? Math.max(0, lowest - appliedCoupon.discount) : lowest;
                return (
                  <div className="flex items-center justify-center gap-3 mt-3 text-xs">
                    {appliedCoupon ? (
                      <>
                        <span className="text-gray-500 line-through text-sm">${lowest}</span>
                        <span className="text-orange-400 font-bold text-sm">${discountedLowest}</span>
                      </>
                    ) : (
                      <span className="text-white font-bold text-sm">{hasOptions ? `Starting at $${lowest}` : `$${Math.round(basePrice)}`}</span>
                    )}
                    <span className="text-gray-500">•</span>
                    <span className="text-gray-400">📦 {fulfillmentDays}-day delivery</span>
                    {expressPrice > 0 && (
                      <>
                        <span className="text-gray-500">•</span>
                        <span className="text-yellow-400">⚡ Optional 24hr delivery</span>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Reviews Carousel — below occasion picker */}
            {reviews.length > 0 && (
              <div className="mb-6">
                <h3 className="text-white font-semibold text-sm mb-3">Reviews</h3>
                <div className="relative">
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 h-[100px] overflow-hidden">
                    <div className="flex items-center gap-1 mb-2">
                      {[...Array(5)].map((_, i) =>
                        i < reviews[reviewIndex]?.rating ? (
                          <StarIcon key={i} className="h-4 w-4 text-yellow-400" />
                        ) : (
                          <StarOutlineIcon key={i} className="h-4 w-4 text-gray-600" />
                        )
                      )}
                    </div>
                    {reviews[reviewIndex]?.comment && (
                      <p className="text-gray-300 text-sm line-clamp-2">"{reviews[reviewIndex].comment}"</p>
                    )}
                    {reviews[reviewIndex]?.users?.full_name && (
                      <p className="text-gray-500 text-xs mt-2">— {reviews[reviewIndex].users?.full_name}</p>
                    )}
                  </div>
                  {reviews.length > 1 && (
                    <div className="flex justify-center gap-1.5 mt-3">
                      {reviews.slice(0, Math.min(reviews.length, 5)).map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setReviewIndex(i)}
                          className={`w-2 h-2 rounded-full transition-all ${i === reviewIndex ? 'bg-white w-4' : 'bg-white/30'}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Recent ShoutOuts Carousel — hidden on Isabella's profile per creator request */}
            {recentShoutouts.length > 0 && username?.toLowerCase() !== 'isabellaismoody' && (
              <div className="mb-6">
                <h3 className="text-white font-semibold text-sm mb-3">Recent ShoutOuts</h3>
                <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory scrollbar-hide">
                  {recentShoutouts.map(shoutout => (
                    <div key={shoutout.id} className="flex-shrink-0 w-40 snap-start">
                      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                        <div className="aspect-[9/16] relative bg-black">
                          <video
                            src={`${shoutout.video_url}#t=0.5`}
                            className="w-full h-full object-cover"
                            playsInline
                            preload="auto"
                            onClick={(e) => {
                              const vid = e.currentTarget;
                              if (vid.paused) {
                                vid.muted = false;
                                vid.play();
                                setPlayingVideoId(shoutout.id);
                              } else {
                                vid.pause();
                                setPlayingVideoId(null);
                              }
                            }}
                            onEnded={() => setPlayingVideoId(null)}
                          />
                          {playingVideoId !== shoutout.id && (
                            <div
                              className="absolute inset-0 flex items-center justify-center pointer-events-none"
                            >
                              <div className="w-10 h-10 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20">
                                <PlayIcon className="h-5 w-5 text-white ml-0.5" />
                              </div>
                            </div>
                          )}
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 p-2 pointer-events-none">
                            <p className="text-white text-xs font-medium truncate">For {shoutout.recipient_name}</p>
                            <p className="text-gray-400 text-[10px]">
                              {OCCASION_CONFIG[shoutout.occasion]?.emoji || '🎬'}{' '}
                              {OCCASION_CONFIG[shoutout.occasion]?.label || shoutout.occasion}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="text-center py-4">
              <a
                href="https://shoutout.us"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 text-xs hover:text-gray-400 transition-colors"
              >
                Powered by ShoutOut
              </a>
            </div>
          </>
        )}

        {/* ============== STEP 2: ORDER ============== */}
        {step === 'order' && selectedOccasion && occasionConfig && (
          <>
            {/* Back button + ShoutOut logo */}
            <div className="flex items-center justify-between mb-5">
              <button
                onClick={handleBackToProfile}
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                <span className="text-sm">Back</span>
              </button>
              <img src="/shoutoutwhite.png" alt="ShoutOut" className="h-6 opacity-60" />
            </div>

            {/* Occasion header */}
            <div className="text-center mb-5">
              <span className="text-4xl">{isScheduled ? '📅' : occasionConfig.emoji}</span>
              <h2 className="text-white text-xl font-bold mt-2">
                {isScheduled ? 'Schedule for a Future Event' : occasionConfig.title}
              </h2>
              {isScheduled && (
                <p className="text-gray-400 text-xs mt-1">Pay $0 today. Card is charged 10 days before your event.</p>
              )}
            </div>

            {/* Scheduled event date picker — shown only for scheduled orders */}
            {isScheduled && (() => {
              const today = new Date();
              const minDate = new Date(today);
              minDate.setDate(today.getDate() + 11);
              const maxDate = new Date(today);
              maxDate.setMonth(today.getMonth() + 6);
              const fmt = (d: Date) => d.toISOString().slice(0, 10);
              return (
                <div
                  className="mb-5 p-4 border"
                  style={{
                    borderRadius: getButtonRadius(),
                    background: `linear-gradient(135deg, ${accentColor}15, ${accentColor}05)`,
                    borderColor: `${accentColor}40`,
                  }}
                >
                  <label className="block text-white text-sm font-semibold mb-2">When is your event?</label>
                  <input
                    type="date"
                    value={scheduledEventDate}
                    onChange={(e) => setScheduledEventDate(e.target.value)}
                    min={fmt(minDate)}
                    max={fmt(maxDate)}
                    className="w-full bg-white/10 border border-white/20 px-3 py-2.5 text-white text-sm focus:outline-none focus:border-white/40"
                    style={{ borderRadius: getButtonRadius(), colorScheme: 'dark' }}
                  />
                  <p className="text-gray-400 text-xs mt-2">
                    Must be at least 11 days away and no more than 6 months out.
                  </p>
                </div>
              );
            })()}

            {/* Talent mini card */}
            <div
              className="flex items-center gap-3 bg-white/5 border border-white/10 p-3 mb-5"
              style={{ borderRadius: getButtonRadius() }}
            >
              <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 border border-white/20">
                {profileImage ? (
                  <img src={profileImage} alt={displayName} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white font-bold" style={{ background: accentColor }}>
                    {displayName[0]}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="text-white font-semibold text-sm truncate">{displayName}</p>
                  {talent.is_verified && <CheckBadgeIcon className="h-4 w-4 text-blue-400 flex-shrink-0" />}
                </div>
                <p className="text-gray-400 text-xs">Personalized video</p>
              </div>
            </div>

            {/* Audio/Video toggle */}
            {talent?.audio_shoutout_enabled && (
              <div className="mb-4 flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                <button onClick={() => setIsAudio(false)} className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${!isAudio ? 'bg-white/15 text-white' : 'text-gray-400'}`}>Video ShoutOut</button>
                <button onClick={() => setIsAudio(true)} className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${isAudio ? 'bg-purple-500/30 text-purple-300 border border-purple-500/30' : 'text-gray-400'}`}>Audio ShoutOut</button>
              </div>
            )}

            {/* Tier selection */}
            {talent?.pricing_mode === 'tiered' && !isAudio && (
              <div className="mb-4">
                <h3 className="text-white font-semibold text-sm mb-3">Choose a package</h3>
                <div className="space-y-2">
                  {[
                    { key: '30s' as const, label: '30 second video', pct: 0.31 },
                    { key: '60s' as const, label: '60 second video', pct: 0.54 },
                    { key: '2min' as const, label: '2 minute video', pct: 1.0 },
                  ].map(t => {
                    const price = Math.round(basePrice * t.pct);
                    const discount = Math.round((1 - t.pct) * 100);
                    return (
                      <button
                        key={t.key}
                        onClick={() => setSelectedTier(t.key)}
                        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-colors ${selectedTier === t.key ? 'border-white/40 bg-white/10' : 'border-white/10 bg-white/5'}`}
                      >
                        <span className="text-white text-sm">{t.label}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-white font-bold">${price}</span>
                          {discount > 0 && <span className="text-green-400 text-xs">{discount}% off</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Express delivery toggle — hidden for scheduled orders since
                payment isn't collected until 10 days before the event */}
            {!isScheduled && talent?.express_delivery_enabled && expressPrice > 0 && (
              <div
                onClick={() => setIsExpress(!isExpress)}
                className={`flex items-center justify-between p-3.5 mb-4 border cursor-pointer transition-all ${
                  isExpress ? 'border-yellow-500/50 bg-yellow-500/10' : 'border-white/10 bg-white/5'
                }`}
                style={{ borderRadius: getButtonRadius() }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg">⚡</span>
                  <div>
                    <p className="text-white font-medium text-sm">Express Delivery (24hrs)</p>
                    <p className="text-gray-400 text-xs">Get your video faster</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {(() => {
                    const expressDelta = Math.round(expressPrice - effectiveBasePrice);
                    if (expressDelta > 0) return <span className="text-yellow-400 font-bold text-sm">+${expressDelta}</span>;
                    if (expressDelta < 0) return <span className="text-green-400 font-bold text-sm">Save ${Math.abs(expressDelta)}</span>;
                    return <span className="text-yellow-400 font-bold text-sm">Included</span>;
                  })()}
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                    isExpress ? 'border-yellow-400 bg-yellow-400' : 'border-gray-500'
                  }`}>
                    {isExpress && (
                      <svg className="w-3 h-3 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Price breakdown */}
            <div
              className="bg-white/5 border border-white/10 p-4 mb-5"
              style={{ borderRadius: getButtonRadius() }}
            >
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-400">{occasionConfig.label}</span>
                <span className={(couponDiscount > 0 || supporterDiscount > 0) ? 'text-gray-500 line-through' : 'text-white'}>${rawOrderPrice.toFixed(2)}</span>
              </div>
              {couponDiscount > 0 && (
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-orange-400">🔥 {appliedCoupon?.discountType === 'percentage' ? `${appliedCoupon.discount}% off` : `$${appliedCoupon?.discount} off`} applied</span>
                  <span className="text-orange-400">-${couponDiscount.toFixed(2)}</span>
                </div>
              )}
              {supporterDiscount > 0 && (
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-green-400">Supporter Discount (10%)</span>
                  <span className="text-green-400">-${supporterDiscount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-400">Processing fee</span>
                <span className="text-white">${processingFee.toFixed(2)}</span>
              </div>
              <div className="border-t border-white/10 pt-2 mt-2 flex justify-between">
                <span className="text-white font-semibold">{isScheduled ? 'Due today' : 'Total'}</span>
                <span className="text-white font-bold text-lg">
                  {isScheduled ? '$0.00' : `$${totalPrice.toFixed(2)}`}
                </span>
              </div>
              {isScheduled && (() => {
                const eventDate = scheduledEventDate ? new Date(scheduledEventDate + 'T00:00:00') : null;
                const chargeDate = eventDate ? new Date(eventDate) : null;
                if (chargeDate) chargeDate.setDate(chargeDate.getDate() - 10);
                return (
                  <div className="mt-1 flex justify-between items-center text-xs">
                    <span className="text-gray-400">Billed later</span>
                    <span className="text-gray-300">
                      ${totalPrice.toFixed(2)}
                      {chargeDate && (
                        <span className="text-gray-500"> on {chargeDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      )}
                    </span>
                  </div>
                );
              })()}
              {addMonthlySupport && parseFloat(monthlySupportAmount) >= 1 && (
                <div className="border-t border-white/10 pt-2 mt-2 flex justify-between text-sm">
                  <span className="text-green-400">+ Monthly support (separate)</span>
                  <span className="text-green-400">${parseFloat(monthlySupportAmount).toFixed(2)}/mo</span>
                </div>
              )}
            </div>

            {/* What to expect */}
            <div
              className="bg-white/5 border border-white/10 p-4 mb-5"
              style={{ borderRadius: getButtonRadius() }}
            >
              <h4 className="text-white font-semibold text-sm mb-3">What to expect</h4>
              <div className="space-y-2.5">
                <div className="flex items-center gap-2.5">
                  <span className="text-lg">🎬</span>
                  <p className="text-white text-sm font-medium">Personal video recorded just for you</p>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="text-lg">📅</span>
                  <p className="text-white text-sm font-medium">
                    {isExpress ? '24-hour delivery' : `Delivered in ~${fulfillmentDays} days`}
                  </p>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="text-lg">✅</span>
                  <p className="text-white text-sm font-medium">100% money-back guarantee</p>
                </div>
              </div>
            </div>

            {/* Email input - hide if already logged in */}
            {emailValid && localStorage.getItem('bio_account_verified') === 'true' ? (
              <div className="mb-4 flex items-center justify-between px-1">
                <p className="text-gray-400 text-sm">Ordering as <span className="text-white font-medium">{guestEmail}</span></p>
                <button type="button" onClick={() => { localStorage.removeItem('bio_account_verified'); setEmailValid(false); }} className="text-xs text-gray-500 hover:text-white">Change</button>
              </div>
            ) : (
              <div className="mb-4">
                <label className="block text-sm text-gray-400 mb-1.5">Enter Email</label>
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  enterKeyHint="go"
                  value={guestEmail}
                  onChange={handleEmailChange}
                  onBlur={validateAndCommitEmail}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      (e.target as HTMLInputElement).blur();
                      void validateAndCommitEmail();
                    }
                  }}
                  placeholder="you@email.com"
                  className="w-full bg-white/5 border border-white/20 px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-white/40 transition-colors"
                  style={{ borderRadius: getButtonRadius() }}
                />
                {guestEmail && emailTouched && !emailValid && (
                  <p className="text-red-400 text-xs mt-1">Please enter a valid email address</p>
                )}
                {emailValid && (
                  <p className="text-green-400 text-xs mt-1">You'll fill in details after payment</p>
                )}
                {/*
                  Explicit Continue button. On mobile, tapping the keyboard's
                  "Done" / "return" doesn't reliably blur the input, so without
                  this the form would sit forever with emailValid=false even
                  though the user has typed a complete address. Visible only
                  while the email hasn't been committed yet.
                */}
                {!emailValid && guestEmail.trim().length > 0 && (
                  <button
                    type="button"
                    onClick={() => void validateAndCommitEmail()}
                    className="mt-3 w-full px-4 py-3 text-white font-medium transition-opacity disabled:opacity-50"
                    style={{
                      borderRadius: getButtonRadius(),
                      background: accentColor,
                      color: getContrastText(accentColor),
                    }}
                  >
                    Continue
                  </button>
                )}
              </div>
            )}

            {/* Apply Credits */}
            {emailValid && userCredits > 0 && (
              <div className="mb-4 p-3 rounded-xl border border-white/10 bg-white/5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white text-sm font-medium">Apply ShoutOut Credits</p>
                    <p className="text-gray-400 text-xs">Balance: ${userCredits.toFixed(2)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setApplyCredits(!applyCredits)}
                    className={`w-12 h-6 rounded-full transition-colors ${applyCredits ? 'bg-green-500' : 'bg-gray-600'}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${applyCredits ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>
                {applyCredits && (
                  <div className="mt-2 pt-2 border-t border-white/10 flex justify-between text-xs">
                    <span className="text-green-400">Credits applied: -${creditsToApply.toFixed(2)}</span>
                    <span className="text-white font-medium">
                      {amountAfterCredits === 0 ? 'Fully covered!' : `Remaining: $${amountAfterCredits.toFixed(2)}`}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Payment container */}
            {emailValid && amountAfterCredits > 0 && (
              <div className="mb-4">

                {checkingCards ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2" style={{ borderColor: accentColor }} />
                  </div>
                ) : savedCards.length > 0 && !useNewCard ? (
                  /* Saved card selection */
                  <div>
                    <p className="text-white text-sm font-medium mb-3">Pay with saved card</p>
                    <div className="space-y-2 mb-4">
                      {savedCards.map((card: any) => (
                        <button
                          key={card.id}
                          onClick={() => setSelectedCardId(card.id)}
                          className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-colors ${
                            selectedCardId === card.id
                              ? 'border-white/40 bg-white/10'
                              : 'border-white/10 bg-white/5 hover:bg-white/8'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-white text-lg">💳</span>
                            <div className="text-left">
                              <p className="text-white text-sm font-medium">
                                {(card.sourceType || 'Card').toUpperCase()} ****{card.lastDigits || '····'}
                              </p>
                              {card.expMonth && <p className="text-gray-400 text-xs">Exp {card.expMonth}/{card.expYear}</p>}
                            </div>
                          </div>
                          {card.isDefault && <span className="text-xs text-gray-400 bg-white/10 px-2 py-0.5 rounded">Default</span>}
                          {selectedCardId === card.id && <span className="text-green-400">✓</span>}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center justify-center gap-3 text-xs text-gray-400 mb-3">
                      <span>🔒 Money-Back Guarantee</span><span>•</span><span>🛡️ Secure</span><span>•</span><span>⚡ Fast</span>
                    </div>
                    {showSlowBanner && isProcessing && (
                      <div className="mb-3 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-400/30 text-amber-200 text-xs text-center leading-relaxed">
                        Taking longer than usual on a slow connection — please don't refresh.
                        Your card will only be charged once.
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={handlePayWithSavedCard}
                      disabled={isProcessing || !selectedCardId || (isScheduled && !scheduledEventDate)}
                      className="w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2"
                      style={{ backgroundColor: accentColor || '#3b82f6', color: getContrastText(accentColor), minHeight: '56px' }}
                    >
                      {isProcessing ? (
                        <><div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> {processingLabel}</>
                      ) : isScheduled ? (
                        <>Schedule Order</>
                      ) : (
                        <>Pay ${amountAfterCredits.toFixed(2)}</>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setUseNewCard(true)}
                      className="w-full text-center text-sm text-gray-400 hover:text-white mt-3 transition-colors"
                    >
                      Add a new card
                    </button>
                  </div>
                ) : (
                  /* New card via Fortis Elements */
                  <div>
                    {savedCards.length > 0 && useNewCard && (
                      <button
                        type="button"
                        onClick={() => { setUseNewCard(false); setCommerceInstance(null); }}
                        className="text-sm text-gray-400 hover:text-white mb-3 transition-colors"
                      >
                        ← Back to saved cards
                      </button>
                    )}
                    <div className="relative w-full overflow-hidden border border-white/10 px-4 pb-4" style={{ backgroundColor: gradStart, borderRadius: getButtonRadius() }}>
                      {!isPaymentReady && (
                        <div
                          className="absolute inset-0 flex items-center justify-center z-10"
                          style={{ backgroundColor: gradStart, borderRadius: getButtonRadius() }}
                        >
                          <div className="flex flex-col items-center gap-3">
                            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2" style={{ borderColor: accentColor }} />
                            <span className="text-gray-400 text-sm">Loading payment form...</span>
                          </div>
                        </div>
                      )}
                      <style>{`
                        #fortis-payment-container button[type="submit"],
                        #fortis-payment-container .submit-button,
                        #fortis-payment-container [class*="SubmitButton"],
                        #fortis-payment-container [class*="submit-btn"],
                        #fortis-payment-container > div > button,
                        #fortis-payment-container h1,
                        #fortis-payment-container h2,
                        #fortis-payment-container h3,
                        #fortis-payment-container [class*="Header"],
                        #fortis-payment-container [class*="header"],
                        #fortis-payment-container [class*="Title"]:first-child,
                        #fortis-payment-container [class*="PaymentInfo"],
                        #fortis-payment-container [class*="payment-info"] {
                          display: none !important;
                        }
                        #fortis-payment-container {
                          overflow: hidden;
                          border-radius: 12px;
                        }
                      `}</style>
                      <div
                        id="fortis-payment-container"
                        ref={iframeContainerRef}
                        className="min-h-[280px] w-full"
                        style={{ marginTop: isTransactionIntent ? '-170px' : '-70px' }}
                      />
                    </div>

                    <div className="flex items-center justify-center gap-3 text-xs text-gray-400 mt-4 mb-3">
                      <span>🔒 Money-Back Guarantee</span><span>•</span><span>🛡️ Secure</span><span>•</span><span>⚡ Fast</span>
                    </div>
                    {showSlowBanner && isProcessing && (
                      <div className="mb-3 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-400/30 text-amber-200 text-xs text-center leading-relaxed">
                        Taking longer than usual on a slow connection — please don't refresh.
                        Your card will only be charged once.
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        if (commerceInstance) {
                          setIsProcessing(true);
                          setPaymentStatus('charging');
                          commerceInstance.submit();
                        } else {
                          toast.error('Payment form is still loading...');
                        }
                      }}
                      disabled={isProcessing}
                      className="w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2"
                      style={{ backgroundColor: accentColor || '#3b82f6', color: getContrastText(accentColor), minHeight: '56px' }}
                    >
                      {isProcessing ? (
                        <><div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> {processingLabel}</>
                      ) : (
                        <>Pay ${amountAfterCredits.toFixed(2)}</>
                      )}
                    </button>

                    <p className="text-center text-gray-400 text-xs mt-3 px-4">
                      We save this card securely through LunarPay for your convenience and future orders with {displayName}.
                    </p>
                  </div>
                )}

                <p className="text-center text-gray-500 text-xs mt-3">
                  Secure payment powered by{' '}
                  <a href="https://lunarpay.com" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-400 transition-colors">
                    LunarPay
                  </a>
                </p>
              </div>
            )}

            {/* Credits-only order (no payment needed) */}
            {emailValid && applyCredits && amountAfterCredits === 0 && (
              <div className="mb-4">
                <button
                  type="button"
                  onClick={async () => {
                    if (!talent || !selectedOccasion) return;
                    setIsProcessing(true);
                    setPaymentStatus('saving');
                    try {
                      const amtCents = Math.round(totalPrice * 100);
                      const feeCents = Math.round(adminFee * 100);
                      const fulfillmentDeadline = new Date();
                      fulfillmentDeadline.setHours(fulfillmentDeadline.getHours() + (talent.fulfillment_time_hours || 168));
                      const { data: newOrderId, error: orderError } = await supabase.rpc('create_guest_order', {
                        p_guest_email: guestEmail.toLowerCase().trim(),
                        p_talent_id: talent.id,
                        p_occasion: selectedOccasion,
                        p_amount: amtCents,
                        p_original_amount: appliedCoupon ? Math.round(rawOrderPrice * 100) : null,
                        p_discount_amount: appliedCoupon ? Math.round(couponDiscount * 100) : null,
                        p_coupon_id: appliedCoupon?.id || null,
                        p_coupon_code: appliedCoupon?.code || null,
                        p_admin_fee: feeCents,
                        p_fulfillment_deadline: fulfillmentDeadline.toISOString(),
                        p_payment_transaction_id: 'CREDITS_ONLY',
                        p_payment_transaction_payload: { credits_applied: creditsToApply },
                        p_promo_source: 'fans',
                        p_is_express_delivery: isExpress,
                      });
                      if (orderError) throw orderError;

                      setOrderId(newOrderId as string);
                      setStep('login');
                      setIsProcessing(false);
                      setPaymentStatus('idle');
                      toast.success('Payment successful!');

                      void (async () => {
                        try {
                          const { data: acctData } = await supabase.functions.invoke('lunarpay-api', { body: { action: 'get-account', email: guestEmail.toLowerCase().trim() } });
                          if (acctData?.user?.id) {
                            await supabase.rpc('use_credits_for_order', { p_user_id: acctData.user.id, p_order_id: newOrderId, p_order_amount_cents: amtCents });
                          }
                          await sendOrderNotifications(newOrderId as string);
                          await processMonthlyDonationAddon();
                        } catch (bgErr) {
                          console.error('Post-success background tasks failed (order is safe):', bgErr);
                        }
                      })();
                    } catch (e: any) {
                      toast.error(e.message || 'Failed to place order');
                      setIsProcessing(false);
                      setPaymentStatus('idle');
                    }
                  }}
                  disabled={isProcessing}
                  className="w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2"
                  style={{ backgroundColor: accentColor || '#3b82f6', color: getContrastText(accentColor), minHeight: '56px' }}
                >
                  {isProcessing ? (
                    <><div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> {processingLabel}</>
                  ) : (
                    <>Place Order (${creditsToApply.toFixed(2)} credits)</>
                  )}
                </button>
              </div>
            )}

            {/* Footer */}
            <div className="pt-4 flex flex-col items-center">
              <p className="text-gray-500 text-xs mb-2">Powered by</p>
              <div className="flex items-center gap-2 opacity-60">
                <img src="/whiteicon.png" alt="ShoutOut" className="h-5 w-5" />
                <span className="text-white font-semibold text-sm">ShoutOut</span>
              </div>
            </div>
          </>
        )}

        {/* ============== STEP 3: LOGIN ============== */}
        {step === 'login' && (
          <LoginStep
            guestEmail={guestEmail}
            accentColor={accentColor}
            getContrastText={getContrastText}
            getButtonRadius={getButtonRadius}
            isScheduled={isScheduled}
            onVerified={() => {
              localStorage.setItem('bio_subscriber_email', guestEmail.toLowerCase().trim());
              localStorage.setItem('bio_account_verified', 'true');
              setStep('details');
            }}
          />
        )}

        {/* ============== STEP 4: DETAILS ============== */}
        {step === 'details' && occasionConfig && (
          <div className="flex flex-col min-h-[80vh]">
            <h2 className="text-xl font-bold text-white mb-6 text-center">Now tell {displayName} what you want!</h2>

            <div className="w-full space-y-5 flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">
                  Who is this video for? <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  placeholder="Enter the recipient's name"
                  className="w-full pl-4 pr-4 py-2.5 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  What would you like {displayName} to mention? <span className="text-red-400">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-2">Add 1-2 things you'd like included in your ShoutOut</p>

                <div className="space-y-3">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">1.</span>
                    <input
                      type="text"
                      value={mention1}
                      onChange={(e) => setMention1(e.target.value)}
                      placeholder="e.g., Wish them a happy birthday"
                      maxLength={160}
                      className="w-full pl-8 pr-16 py-2.5 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs">{mention1.length}/160</span>
                  </div>

                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">2.</span>
                    <input
                      type="text"
                      value={mention2}
                      onChange={(e) => setMention2(e.target.value)}
                      placeholder="e.g., They're a huge fan of your podcast (optional)"
                      maxLength={160}
                      className="w-full pl-8 pr-16 py-2.5 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs">{mention2.length}/160</span>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={async () => {
                if (!recipientName.trim()) {
                  toast.error('Please enter the recipient\'s name');
                  return;
                }
                if (!mention1.trim()) {
                  toast.error('Please add what they should mention');
                  return;
                }
                setIsProcessing(true);
                try {
                  const { error } = await supabase.rpc('update_order_details', {
                    p_order_id: orderId,
                    p_recipient_name: recipientName.trim(),
                    p_request_details: [mention1, mention2].filter(Boolean).join('\n'),
                  });
                  if (error) throw error;
                  toast.success('Details submitted!');
                  navigate('/account');
                } catch (err: any) {
                  console.error('Submit details error:', err);
                  toast.error(err?.message || 'Failed to submit details');
                } finally {
                  setIsProcessing(false);
                }
              }}
              disabled={isProcessing}
              className="w-full py-3.5 font-semibold text-sm flex items-center justify-center gap-2 mt-6 transition-all hover:opacity-90"
              style={{ backgroundColor: accentColor, color: getContrastText(accentColor), borderRadius: getButtonRadius() }}
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Submitting...
                </>
              ) : (
                'Submit Details'
              )}
            </button>

            <div className="pt-8 flex flex-col items-center">
              <p className="text-gray-500 text-xs mb-2">Powered by</p>
              <div className="flex items-center gap-2 opacity-60">
                <img src="/whiteicon.png" alt="ShoutOut" className="h-5 w-5" />
                <span className="text-white font-semibold text-sm">ShoutOut</span>
              </div>
            </div>
          </div>
        )}

        {/* ============== STEP 5: COMPLETE ============== */}
        {step === 'complete' && (
          <div className="flex flex-col items-center justify-center min-h-[80vh] text-center">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
              style={{ backgroundColor: `${accentColor}20` }}
            >
              <svg className="w-10 h-10" style={{ color: accentColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h2 className="text-2xl font-bold text-white mb-2">You're all set!</h2>
            {isScheduled && scheduledEventDate ? (() => {
              const eventDate = new Date(scheduledEventDate + 'T00:00:00');
              const chargeDate = new Date(eventDate);
              chargeDate.setDate(chargeDate.getDate() - 10);
              return (
                <p className="text-gray-400 mb-6 max-w-xs">
                  We'll charge your card{' '}
                  <span className="text-white font-medium">${totalPrice.toFixed(2)}</span>{' '}
                  on{' '}
                  <span className="text-white font-medium">{chargeDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}</span>{' '}
                  and your video will be delivered to{' '}
                  <span className="text-white font-medium">{guestEmail}</span>{' '}
                  before{' '}
                  <span className="text-white font-medium">{eventDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}</span>.
                </p>
              );
            })() : (
              <p className="text-gray-400 mb-6 max-w-xs">
                Your personalized video will be delivered to{' '}
                <span className="text-white font-medium">{guestEmail}</span>{' '}
                within {isExpress ? '24 hours' : `~${fulfillmentDays} days`}.
              </p>
            )}

            <button
              onClick={() => {
                localStorage.setItem('bio_subscriber_email', guestEmail.toLowerCase().trim());
                navigate('/account');
              }}
              className="w-full max-w-sm py-3 font-semibold transition-all hover:opacity-90"
              style={{ backgroundColor: accentColor, color: getContrastText(accentColor), borderRadius: getButtonRadius() }}
            >
              View My Orders
            </button>

            <div className="pt-6 flex flex-col items-center">
              <p className="text-gray-500 text-xs mb-2">Powered by</p>
              <div className="flex items-center gap-2 opacity-60">
                <img src="/whiteicon.png" alt="ShoutOut" className="h-5 w-5" />
                <span className="text-white font-semibold text-sm">ShoutOut</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* OTP Login Modal */}
      {/* FOMO notification — cycles through recent orders */}
      {fomoOrders.length > 0 && fomoIndex >= 0 && (
        <div
          className={`fixed left-3 right-3 sm:left-auto sm:right-4 z-[60] transition-all duration-500 ease-in-out transform ${
            fomoVisible ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0 pointer-events-none'
          }`}
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)', maxWidth: '260px' }}
        >
          <div
            className="rounded-lg px-3 py-2 border backdrop-blur-xl shadow-lg"
            style={{
              background: `linear-gradient(135deg, ${gradStart}f2, ${gradEnd}f2)`,
              borderColor: `${accentColor}50`,
            }}
          >
            <p className="text-xs text-white/95 font-medium leading-snug">
              <span className="font-semibold">{fomoOrders[fomoIndex].name}</span> just ordered from {firstName}
            </p>
            <div className="flex items-center gap-1.5 mt-1">
              <span className="text-[10px] text-gray-400">
                {(() => {
                  const d = new Date(fomoOrders[fomoIndex].created_at);
                  const now = new Date();
                  const diffMs = now.getTime() - d.getTime();
                  const diffDays = Math.floor(diffMs / 86400000);
                  if (diffDays === 0) return 'Today';
                  if (diffDays === 1) return 'Yesterday';
                  if (diffDays < 7) return `${diffDays}d ago`;
                  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                })()}
              </span>
              <span className="text-gray-600">•</span>
              <CheckBadgeIcon className="h-3 w-3" style={{ color: accentColor }} />
              <span className="text-[10px] font-medium" style={{ color: accentColor }}>Verified by ShoutOut</span>
            </div>
          </div>
        </div>
      )}

      {showOtpModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold text-white text-center mb-1">Welcome back!</h3>
            <p className="text-gray-400 text-sm text-center mb-5">Verify your identity to access your saved cards and account</p>

            {!otpSent && otpNeedsPhone ? (
              <div className="space-y-3">
                <input type="tel" value={otpPhone} onChange={e => setOtpPhone(e.target.value)} placeholder="Phone number"
                  className="w-full bg-white/5 border border-white/20 px-4 py-3 rounded-xl text-white placeholder-gray-500 focus:outline-none text-center text-lg" />
                <button onClick={handleOtpSend} disabled={otpLoading}
                  className="w-full py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {otpLoading ? <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> Sending...</> : 'Send Code'}
                </button>
              </div>
            ) : otpSent ? (
              <div className="space-y-3">
                <p className="text-gray-400 text-sm text-center">Code sent to {otpPhoneHint || '***'}</p>
                <input type="text" value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000" maxLength={6} autoFocus
                  className="w-full bg-white/5 border border-white/20 px-4 py-3 rounded-xl text-white text-center text-2xl tracking-[0.5em] placeholder-gray-500 focus:outline-none font-mono"
                  onKeyDown={e => { if (e.key === 'Enter') handleOtpVerify(); }} />
                <button onClick={handleOtpVerify} disabled={otpLoading}
                  className="w-full py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {otpLoading ? <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> Verifying...</> : 'Verify & Continue'}
                </button>
                <button onClick={() => { setOtpSent(false); setOtpCode(''); setOtpNeedsPhone(true); }}
                  className="w-full text-center text-sm text-gray-400 hover:text-white">Change number</button>
              </div>
            ) : (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent" />
              </div>
            )}

            <button onClick={() => { setShowOtpModal(false); }}
              className="w-full mt-4 text-center text-sm text-gray-500 hover:text-white">Skip — pay with new card</button>
          </div>
        </div>
      )}
    </div>
  );
};

const LoginStep: React.FC<{
  guestEmail: string;
  accentColor: string;
  getContrastText: (hex: string) => string;
  getButtonRadius: () => string;
  onVerified: () => void;
  isScheduled?: boolean;
}> = ({ guestEmail, accentColor, getContrastText, getButtonRadius, onVerified, isScheduled }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneHint, setPhoneHint] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(true);
  const [needsPhone, setNeedsPhone] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  useEffect(() => {
    const checkEmail = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('send-registration-otp', {
          body: { email: guestEmail.toLowerCase().trim(), checkEmailOnly: true },
        });
        if (error) throw error;
        if (data?.sentToExistingPhone) {
          setPhoneHint(data.phoneHint || '');
          setPhoneNumber(data.phone || '');
          setOtpSent(true);
          toast.success('Code sent to ' + (data.phoneHint || 'your phone'));
        } else {
          setNeedsPhone(true);
        }
      } catch (err) {
        console.error('Check email error:', err);
        setNeedsPhone(true);
      } finally {
        setCheckingEmail(false);
      }
    };
    checkEmail();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestEmail]);

  const sendOtp = async () => {
    if (!phoneNumber.trim()) { toast.error('Please enter your phone number'); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-registration-otp', {
        body: { email: guestEmail.toLowerCase().trim(), phone: phoneNumber },
      });
      if (error) throw error;
      setPhoneHint(data?.phoneHint || phoneNumber.slice(-4));
      setOtpSent(true);
      toast.success('Code sent!');
    } catch (err: any) { toast.error(err?.message || 'Failed to send code'); }
    finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    if (otpCode.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke('verify-registration-otp', {
        body: { email: guestEmail.toLowerCase().trim(), phone: phoneNumber, code: otpCode },
      });
      if (error) throw error;
      toast.success('Verified!');
      onVerified();
    } catch (err: any) { toast.error(err?.message || 'Invalid code'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="w-full max-w-sm flex flex-col items-center">
        <div className="w-full py-3 px-4 mb-6 text-center font-semibold text-sm" style={{ backgroundColor: accentColor + '20', color: accentColor, borderRadius: getButtonRadius() }}>
          {isScheduled ? 'Order Scheduled! 🎉' : 'Payment successful! 🎉'}
        </div>
        <h2 className="text-xl font-bold text-white mb-1">Verify your identity</h2>
        <p className="text-gray-400 text-sm mb-6">to add order details</p>
        {checkingEmail ? (
          <div className="flex items-center gap-2 text-gray-400">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-400 border-t-transparent" />
            <span className="text-sm">Checking account...</span>
          </div>
        ) : !otpSent && needsPhone ? (
          <div className="w-full space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1.5">Phone number</label>
              <input type="tel" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} placeholder="(555) 123-4567"
                className="w-full bg-white/5 border border-white/20 px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-white/40 text-center text-lg"
                style={{ borderRadius: getButtonRadius() }} />
            </div>
            <button onClick={sendOtp} disabled={loading}
              className="w-full py-3.5 font-semibold text-sm flex items-center justify-center gap-2"
              style={{ backgroundColor: accentColor, color: getContrastText(accentColor), borderRadius: getButtonRadius() }}>
              {loading ? <><div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent" /> Sending...</> : 'Send Code'}
            </button>
          </div>
        ) : otpSent ? (
          <div className="w-full space-y-4">
            <p className="text-gray-400 text-sm text-center">Code sent to <span className="text-white font-medium">{phoneHint || phoneNumber}</span></p>
            <input type="text" value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000" maxLength={6} autoFocus
              className="w-full bg-white/5 border border-white/20 px-4 py-3 text-white text-center text-2xl tracking-[0.5em] placeholder-gray-500 focus:outline-none focus:border-white/40 font-mono"
              style={{ borderRadius: getButtonRadius() }} />
            <button onClick={verifyOtp} disabled={loading}
              className="w-full py-3.5 font-semibold text-sm flex items-center justify-center gap-2"
              style={{ backgroundColor: accentColor, color: getContrastText(accentColor), borderRadius: getButtonRadius() }}>
              {loading ? <><div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent" /> Verifying...</> : 'Verify'}
            </button>
            <button onClick={() => { setOtpSent(false); setOtpCode(''); setNeedsPhone(true); }}
              className="w-full text-center text-sm text-gray-400 hover:text-white underline">Change number</button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ShoutOutProfilePage;
